import { Component, HostListener, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { RoType, checklistAssig } from '../../../interface/ro-type';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { RoTypeService } from '../../../service/ro-type.service';
import { LayoutService } from '../../../service/layout.service';
import { SnackbarService } from '../../../service/snackbar.service';
import { RoTypeUtilities } from '../../../class/ro-type-utilities';
import { DataService } from '../../../service/data.service';
import { Subject, catchError, debounceTime, from, merge, of, switchMap } from 'rxjs';
import { Company } from '../../../interface/company';
import { CompanyService } from '../../../service/company.service';
import { Utilities } from '../../../class/utilities';
import { Operation } from '../../../interface/operation';
import { UnsubscribeAll } from '../../../class/unsubscribe-all';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatAutocompleteModule, MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { ErrorPipe } from 'src/app/shared/pipes/error.pipe';
import { SingleClickDirective } from 'src/app/shared/directives/single-click.directive';
import { MatButton } from '@angular/material/button';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { BOOLEAN_NUMBER } from '@app/shared/common-enums/boolean-number';
import { ChecklistMediaQualityPreset, resolveChecklistMediaQualityPreset } from '@app/shared/config/checklist-media-quality.config';

@Component({
  selector: 'app-ro-type-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,

    TranslateModule,
    ErrorPipe,
    SingleClickDirective,

    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatFormField,
    MatInput,
    MatLabel,
    MatError,
    MatAutocompleteModule,
    MatSelect,
    MatOption,
    MatButton,
    MatHint,
    MatSlideToggle
  ],
  templateUrl: './ro-type-edit.component.html',
  styleUrl: './ro-type-edit.component.scss'
})
export class RoTypeEditComponent extends UnsubscribeAll implements OnInit, OnDestroy {
  readonly BOOLEAN_NUMBER = BOOLEAN_NUMBER;
  readonly checklistMediaQualityPresetOptions: Array<{ value: ChecklistMediaQualityPreset; label: string }> = [
    { value: 'default', label: 'Padrão' },
    { value: 'high', label: 'Alta (Upload da mídia e fechamento da os mais lentos)' },
  ];
  /** options of each CNPJ field, searched on the server: the shared company lists may be partial */
  nfeCompanies: Company[] = [];
  nfseCompanies: Company[] = [];
  private _focus$ = { nfeCnpj: new Subject<void>(), nfseCnpj: new Subject<void>() };
  private _companyService = inject(CompanyService);

  form: FormGroup = this.formBuilder.group({
    id: [''],
    type: ['', [Validators.required]],
    observation: [''],
    nfseCnpj: ['', [Utilities.validateCNPJ()]],
    nfeCnpj: ['', [Utilities.validateCNPJ()]],
    separateFinancial: [true],
    operation: [''],
    checklistAssig: [false, [Validators.required]],
    checklistMediaQualityPreset: ['default' as ChecklistMediaQualityPreset, [Validators.required]],
    active: [BOOLEAN_NUMBER.TRUE]
  }, {

    validators: [
      Utilities.distinctControls('nfseCnpj', 'nfeCnpj'),
      this.operationRequiredIfFilial(() => this.isFilial)
    ]
  });

  isFilial = false;

  private roType: RoType;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { roType: RoType, operations: Operation[] },
    public dialogRef: MatDialogRef<RoTypeEditComponent>,
    private roTypeService: RoTypeService,
    private layout: LayoutService,
    private snackbar: SnackbarService,
    private formBuilder: FormBuilder,
    private _dataservice: DataService
  ) {
    super();
    if (data && data.roType) {
      this.roType = {
        ...data.roType,
        checklistMediaQualityPreset: resolveChecklistMediaQualityPreset(data.roType.checklistMediaQualityPreset),
        nfeCnpj: data.roType.nfeCnpj?.trim() ? data.roType.nfeCnpj : "",
        observation: data.roType.observation?.trim() ? data.roType.observation : "",
        nfseCnpj: data.roType.nfseCnpj?.trim() ? data.roType.nfseCnpj : "",
      };
    }

    this.isFilial = this._dataservice?.company?.cnpjMatrix ? true : false;
  }

  ngOnInit() {
    if (this.roType) {
      this.form.patchValue(this.roType);
      this.form.get('checklistAssig').setValue(this.roType.checklistAssig === 1 ? false : true);
    }
    this.dialogRef.backdropClick().subscribe(() => this.closeIfUnchanged());

    // on input nfse CNPJ, define default nfe CNPJ
    this.subs.push(
      this.form.get('nfseCnpj').valueChanges.subscribe(value => {
        if(value){
          this.form.get('nfeCnpj').setValue(this._dataservice.company.cnpj, { emitEvent: false });
        }
      }))

    // on input nfe CNPJ, define default nfse CNPJ
    this.subs.push(
      this.form.get('nfeCnpj').valueChanges.subscribe(value => {
        if(value){
          this.form.get('nfseCnpj').setValue(this._dataservice.company.cnpj, { emitEvent: false });
        }
      })
    )

    this._bindCompanySearch('nfeCnpj', companies => this.nfeCompanies = companies);
    this._bindCompanySearch('nfseCnpj', companies => this.nfseCompanies = companies);
  }

  /** Focusing an empty field loads the first companies in the API order (favorites, recent, name) */
  loadCompanies(field: 'nfeCnpj' | 'nfseCnpj') {
    this._focus$[field].next();
  }

  /** Searches the user's companies on the server for what is typed; a late answer to an older search is dropped */
  private _bindCompanySearch(field: 'nfeCnpj' | 'nfseCnpj', setOptions: (companies: Company[]) => void) {
    const control = this.form.get(field);
    this.subs.push(merge(
      control.valueChanges.pipe(debounceTime(300)),
      this._focus$[field]
    ).pipe(
      switchMap(() => from(this._companyService.getAll((control.value || '').trim() || undefined)).pipe(
        catchError(() => of([] as Company[]))
      ))
    ).subscribe(setOptions));
  }

  override ngOnDestroy(): void {
    this.unsubscribeAll()
  }

  operationRequiredIfFilial(isFilialFn: () => boolean): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const isFilial = isFilialFn();
      const operation = group.get('operation');

      if (isFilial && (!operation?.value || operation.value.trim() === '')) {
        operation?.setErrors({ required: true });
        return { operationRequired: true };
      } else {
        if (operation?.hasError('required')) {
          operation.setErrors(null);
        }
        return null;
      }
    };
  }

  async onSubmit() {
    this.layout.loader = true;
    let roType: RoType = this.form.getRawValue();
    try {
      roType.checklistAssig = this.form.get('checklistAssig').value ? 2 : 1;
      roType.nfeCnpj = roType.nfeCnpj.trim();
      roType.nfseCnpj = roType.nfseCnpj.trim();
      if (roType.id) {
        await this.roTypeService.update(roType)
        this.snackbar.success('RO_TYPE.UPDATED', { code: this.roType.code });
      } else {
        const id = await this.roTypeService.add(roType);
        roType["id"] = id;
        this.snackbar.success('RO_TYPE.ADDED');
      }
      this.dialogRef.close(roType);
    } catch (error) {
      this.layout.loader = false;
      this.snackbar.error(roType.id ? 'RO_TYPE.NOT_UPDATED' : 'RO_TYPE.NOT_ADDED', { code: this.roType.code }, error);
    } finally {
      this.layout.loader = false;
    }
  }

  @HostListener('window:keyup.esc')
  onKeyUp() {
    this.closeIfUnchanged();
  }

  private closeIfUnchanged() {
    if (!RoTypeUtilities.changed(this.roType, this.form.value)) {
      this.dialogRef.close();
    }
  }

  protected toggleActive(): void {
    const currentActiveValue: BOOLEAN_NUMBER = this.form.get('active').value;
    this.form.get('active').setValue(currentActiveValue === BOOLEAN_NUMBER.TRUE ? BOOLEAN_NUMBER.FALSE : BOOLEAN_NUMBER.TRUE);
  }
}
